import { MarkerClusterer } from '@googlemaps/markerclusterer';

type KioskLocation = {
    retailerId: string;
    latitude: number;
    longitude: number;
    formattedAddress: string;
    monthlyVisitsCount: number;
    subCategoryId: string;
    availableAt?: string | null;
};

type Config = {
    apiKey: string;
    locationsUrl: string;
    categoriesUrl: string;
    locale: string;
    labels: Record<string, string>;
};

const kioskSvg = (fill: string) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="64" viewBox="0 0 48 64">
        <path d="M24 1C11.3 1 1 11.3 1 24c0 17.3 23 39 23 39s23-21.7 23-39C47 11.3 36.7 1 24 1Z" fill="${fill}" stroke="#fff" stroke-width="1.5"/>
        <circle cx="24" cy="24" r="9.5" fill="#fff"/>
    </svg>
`)}`;

const iconNow = kioskSvg('#000000');
const iconFuture = kioskSvg('#e5ab00');
const iconCluster = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r="28" fill="#677078" fill-opacity=".55"/>
        <circle cx="32" cy="32" r="24" fill="#000" stroke="#fff" stroke-width="1.5"/>
    </svg>
`)}`;

// Google Maps Loader Singleton
let mapsPromise: Promise<any> | null = null;
function loadGoogleMaps(apiKey: string): Promise<any> {
    if ((window as any).google?.maps) return Promise.resolve((window as any).google.maps);
    if (!mapsPromise) {
        mapsPromise = new Promise((resolve, reject) => {
            const cb = `initGMap_${Date.now()}`;
            (window as any)[cb] = () => { delete (window as any)[cb]; resolve((window as any).google.maps); };
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${cb}&loading=async&libraries=places`;
            script.async = true;
            script.onerror = () => reject(new Error('Google Maps could not be loaded.'));
            document.head.appendChild(script);
        });
    }
    return mapsPromise;
}

function getDistanceKm(from: { lat: number; lng: number }, loc: KioskLocation): number {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(loc.latitude - from.lat);
    const dLng = toRad(loc.longitude - from.lng);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(from.lat)) * Math.cos(toRad(loc.latitude)) * Math.sin(dLng / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function kioskMap(config: Config) {
    return {
    config,
    searchQuery: '',
    statusMessage: '',
    errorMessage: '',
    isSearching: false,
    isLocating: false,
    hasResults: false,
    locationsDialogOpen: false,
    visibleCount: 12,
    
    locations: [] as KioskLocation[],
    sortedLocations: [] as Array<{ location: KioskLocation; distanceKm: number }>,
    subCategoryLabels: new Map<string, string>(),

    maps: null as any,
    map: null as any,
    infoWindow: null as any,
    locationMarkers: new Map<string, any>(),
    selectedLocationId: null as string | null,
    directionsOrigin: null as { lat: number; lng: number } | null,

    get visibleLocations() {
        return this.sortedLocations.slice(0, this.visibleCount);
    },

    openLocationsDialog() {
        this.locationsDialogOpen = true;
        this.$nextTick(() => this.$refs.locationsDialogClose?.focus());
    },

    closeLocationsDialog() {
        this.locationsDialogOpen = false;
        if (window.matchMedia('(max-width: 1023px)').matches) {
            this.$nextTick(() => this.$refs.locationsDialogTrigger?.focus());
        }
    },

    async init() {
        if (!config.apiKey || !config.locationsUrl || !config.categoriesUrl) {
            this.errorMessage = 'Missing configuration parameters.';
            return;
        }

        try {
            const [maps, locRes, catRes] = await Promise.all([
                loadGoogleMaps(config.apiKey),
                fetch(config.locationsUrl, { headers: { Accept: 'application/json' } }),
                fetch(config.categoriesUrl, { headers: { Accept: 'application/json' } })
            ]);

            if (!locRes.ok || !catRes.ok) throw new Error('Failed to load map data.');

            this.maps = maps;
            const locationsData = await locRes.json() as KioskLocation[];
            const categoriesData = await catRes.json();
            const lang = config.locale.split('-')[0];
            for (const cat of categoriesData.data || []) {
                for (const sub of cat.subCategories || []) {
                    const name = sub.name?.[lang] || sub.name?.en;
                    if (name) this.subCategoryLabels.set(sub.id, name);
                }
            }

            this.locations = locationsData.filter(l => Number.isFinite(l.latitude) && Number.isFinite(l.longitude) && l.formattedAddress);
            if (!this.locations.length) throw new Error('No kiosk locations available.');

            this.initMap();
            this.initAutocomplete();
        } catch (err: any) {
            this.errorMessage = err.message || 'Error loading map.';
        }
    },

    initMap() {
        this.map = new this.maps.Map(this.$refs.mapCanvas, {
            center: { lat: this.locations[0].latitude, lng: this.locations[0].longitude },
            zoom: 10,
            mapTypeControl: false,
            fullscreenControl: true,
            streetViewControl: true,
            zoomControl: true,
        });

        // Add Legend (HTML string instead of DOM elements)
        const legend = document.createElement('div');
        legend.className = 'map-legend';
        legend.innerHTML = `
            <h4 class="text-base leading-none font-semibold mb-3">${this.config.labels.legendTitle}</h4>
            <div class="flex items-center gap-3 mb-2">
                <img src="${iconNow}" width="16" height="24" alt="" />
                <span class="text-sm">${this.config.labels.availNow}</span>
            </div>
            <div class="flex items-center gap-3">
                <img src="${iconFuture}" width="16" height="24" alt="" />
                <span class="text-sm">${this.config.labels.availFuture}</span>
            </div>
        `;
        this.map.controls[this.maps.ControlPosition.TOP_LEFT].push(legend);

        const bounds = new this.maps.LatLngBounds();
        const infoWindowTitle = document.createElement('h3');
        infoWindowTitle.className = 'map-info-title h6';
        infoWindowTitle.textContent = this.config.labels.infoTitle;

        this.infoWindow = new this.maps.InfoWindow({
            ariaLabel: this.config.labels.infoTitle,
            headerContent: infoWindowTitle,
        });
        this.infoWindow.addListener('closeclick', () => {
            this.selectedLocationId = null;
        });
        this.locationMarkers.clear();
        const now = new Date();

        const markers = this.locations.map(location => {
            const pos = { lat: location.latitude, lng: location.longitude };
            const isFuture = location.availableAt ? new Date(location.availableAt) > now : false;
            
            const marker = new this.maps.Marker({
                position: pos,
                title: location.formattedAddress,
                icon: {
                    url: isFuture ? iconFuture : iconNow,
                    scaledSize: new this.maps.Size(36, 48),
                    anchor: new this.maps.Point(18, 47.25),
                }
            });

            marker.addListener('click', () => {
                this.openInfoWindow(location, marker);
            });

            this.locationMarkers.set(location.retailerId, marker);
            bounds.extend(pos);
            return marker;
        });

        new MarkerClusterer({
            map: this.map,
            markers,
            renderer: {
                render: ({ count, position }: any) => new this.maps.Marker({
                    position,
                    icon: {
                        url: iconCluster,
                        scaledSize: new this.maps.Size(48, 48),
                        anchor: new this.maps.Point(24, 24),
                        labelOrigin: new this.maps.Point(24, 24),
                    },
                    label: { text: String(count), color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: '16.5px', fontWeight: '700' },
                    zIndex: 1000 + count,
                })
            }
        });

        this.map.fitBounds(bounds, 48);
    },

    selectLocation(location: KioskLocation) {
        const marker = this.locationMarkers.get(location.retailerId);
        if (!this.map || !marker) return;

        this.map.moveCamera({
            center: { lat: location.latitude, lng: location.longitude },
            zoom: 15,
        });
        this.openInfoWindow(location, marker);

        if (this.locationsDialogOpen) this.closeLocationsDialog();
    },

    openInfoWindow(location: KioskLocation, marker: any) {
        this.selectedLocationId = location.retailerId;
        this.infoWindow.setContent(`
            <article class="map-info-card text-sm space-y-1">
                <p class="text-base flex items-center gap-2"><i class="fa-solid fa-location-dot"></i> <span>${location.formattedAddress}</span></p>
                <p class="text-base flex items-center gap-2"><i class="fa-solid fa-eye"></i> <strong class="text-[#115e59] font-bold">${this.formatNumber(location.monthlyVisitsCount)}</strong> ${this.config.labels.impressions}</p>
                <p class="text-base flex items-center gap-2"><i class="fa-solid fa-tag"></i> <span>${this.getCategoryLabel(location.subCategoryId)}</span></p>
            </article>
        `);
        this.infoWindow.open({ anchor: marker, map: this.map });
    },

    initAutocomplete() {
        if (!this.maps.places?.Autocomplete) return;
        const autocomplete = new this.maps.places.Autocomplete(this.$refs.searchInput, {
            componentRestrictions: { country: 'ca' },
            fields: ['formatted_address', 'geometry', 'name'],
            types: ['geocode'],
        });

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            const loc = place.geometry?.location;

            if (!loc) {
                this.statusMessage = this.config.labels.selectSuggestion;
                return;
            }

            this.searchQuery = place.formatted_address || place.name || this.searchQuery;
            this.focusLocation({ lat: loc.lat(), lng: loc.lng() });
        });
    },

    onSearchInput() {
        if (!this.searchQuery) {
            this.hasResults = false;
            this.locationsDialogOpen = false;
            this.statusMessage = '';
            this.triggerResize();
        }
    },

    searchLocation() {
        if (!this.searchQuery.trim()) {
            this.statusMessage = this.config.labels.enterLocation;
            this.$refs.searchInput.focus();
            return;
        }

        this.isSearching = true;
        this.statusMessage = this.config.labels.searching;

        new this.maps.Geocoder().geocode(
            { address: this.searchQuery, componentRestrictions: { country: 'CA' } },
            (results: any[], status: string) => {
                this.isSearching = false;
                if (status === 'OK' && results?.[0]) {
                    const loc = results[0].geometry.location;
                    this.focusLocation({ lat: loc.lat(), lng: loc.lng() });
                } else {
                    this.statusMessage = this.config.labels.locationNotFound;
                }
            }
        );
    },

    locateUser() {
        if (!navigator.geolocation) return;
        this.isLocating = true;
        this.statusMessage = this.config.labels.findingLocation;

        navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
                this.isLocating = false;
                const pos = { lat: coords.latitude, lng: coords.longitude };
                this.focusLocation(pos);

                new this.maps.Geocoder().geocode({ location: pos }, (results: any[], status: string) => {
                    if (status !== 'OK' || !results?.[0]) return;
                    const comps = results[0].address_components || [];
                    const city = comps.find((c: any) => c.types?.some((t: string) => ['locality', 'postal_town', 'administrative_area_level_3'].includes(t)))?.long_name;
                    const prov = comps.find((c: any) => c.types?.includes('administrative_area_level_1'))?.short_name;
                    if (city && prov) this.searchQuery = `${city}, ${prov}`;
                });
            },
            () => {
                this.isLocating = false;
                this.statusMessage = this.config.labels.locationAccessDenied;
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    },

    focusLocation(pos: { lat: number; lng: number }) {
        this.directionsOrigin = pos;
        this.map.moveCamera({
            center: pos,
            zoom: 12,
        });

        this.hasResults = true;
        this.visibleCount = 12;

        this.sortedLocations = this.locations
            .map(location => ({
                location,
                distanceKm: getDistanceKm(pos, location),
            }))
            .sort((a, b) => a.distanceKm - b.distanceKm);

        this.statusMessage = this.config.labels.mapCentered;
        this.triggerResize();
        this.$nextTick(() => {
            this.$refs.locationsListDesktop?.scrollTo({ top: 0 });
        });
    },

    triggerResize() {
        this.$nextTick(() => {
            this.maps.event.trigger(this.map, 'resize');
        });
    },

    formatNumber(num: number): string {
        return new Intl.NumberFormat(this.config.locale || 'en').format(num);
    },

    formatDistance(km: number): string {
        return `${new Intl.NumberFormat(this.config.locale || 'en', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(km)} km`;
    },

    getDirectionsUrl(location: KioskLocation): string {
        const params = new URLSearchParams({
            api: '1',
            destination: location.formattedAddress,
        });

        if (this.directionsOrigin) {
            params.set('origin', `${this.directionsOrigin.lat},${this.directionsOrigin.lng}`);
        }

        return `https://www.google.com/maps/dir/?${params.toString()}`;
    },

    getCategoryLabel(id: string): string {
        return this.subCategoryLabels.get(id) || id;
    }
    };
}
